import { useEffect, useRef, useState } from "react";
import { useIsAuthenticated } from "@azure/msal-react";
import { useAuth } from "./lib/useAuth";
import { AppBar, SideNav, useMediaQuery } from "./components/AppShell";
import { IconSprite } from "./components/Icon";
import { APP_NAME, DEFAULT_VIEW, findView } from "./nav";
import ActivityView from "./views/ActivityView";
import PromptsView from "./views/PromptsView";
import ChatView from "./views/ChatView";
import AnalyticsView from "./views/AnalyticsView";
import "./styles/tokens.css";
import "./index.css";

const VIEWS = {
  activity: ActivityView,
  prompts: PromptsView,
  chats: ChatView,
  analytics: AnalyticsView,
};

// The drawer is persistent at and above this width, temporary below it.
const DESKTOP_QUERY = "(min-width: 960px)";
const COMPACT_KEY = "claudelogs.sidenav.compact";

function viewFromHash() {
  const id = window.location.hash.replace(/^#\/?/, "");
  return VIEWS[id] ? id : DEFAULT_VIEW;
}

function SignInScreen({ onLogin }) {
  return (
    <>
      <AppBar />
      <main className="signin">
        <div className="signin__panel">
          <h1>{APP_NAME}</h1>
          <p className="signin__desc">
            Sign in with your EPLP account to review Claude activity, prompts, and usage.
          </p>
          <button className="btn btn--primary signin__button" type="button" onClick={onLogin}>
            Sign in with Microsoft
          </button>
        </div>
      </main>
    </>
  );
}

export default function App() {
  const isAuthenticated = useIsAuthenticated();
  const { account, login, logout } = useAuth();
  const [view, setView] = useState(viewFromHash);
  const [compact, setCompact] = useState(() => localStorage.getItem(COMPACT_KEY) === "true");
  const [navOpen, setNavOpen] = useState(false);
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const menuButtonRef = useRef(null);

  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    document.title = isAuthenticated ? `${findView(view).label} — ${APP_NAME}` : `Sign in — ${APP_NAME}`;
  }, [view, isAuthenticated]);

  useEffect(() => {
    localStorage.setItem(COMPACT_KEY, String(compact));
  }, [compact]);

  useEffect(() => {
    if (isDesktop) setNavOpen(false);
  }, [isDesktop]);

  // The temporary drawer closes on Escape and hands focus back to its trigger.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = e => {
      if (e.key === "Escape") {
        setNavOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);

  if (!isAuthenticated) {
    return (
      <>
        <IconSprite />
        <SignInScreen onLogin={login} />
      </>
    );
  }

  // Compact is a desktop-only state; the temporary drawer is always full width.
  const railCompact = compact && isDesktop;
  const View = VIEWS[view];

  return (
    <>
      <IconSprite />
      <AppBar
        account={account}
        onSignOut={logout}
        navOpen={navOpen}
        onMenu={() => setNavOpen(o => !o)}
        menuButtonRef={menuButtonRef}
      />
      <div className={`shell${railCompact ? " shell--compact" : ""}`}>
        <SideNav
          current={view}
          compact={railCompact}
          open={navOpen}
          onToggleCompact={() => setCompact(c => !c)}
          onNavigate={() => setNavOpen(false)}
          footer="SQL warehouse · polled every 15 min"
        />
        {navOpen && <div className="nav-scrim" onClick={() => setNavOpen(false)} />}
        <main className="main">
          <View key={view} />
        </main>
      </div>
    </>
  );
}
